import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { capDisplayFreshness } from '../../utils/freshnessDisplay';

// Add fadeIn animation styles
const fadeInStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes imageReveal {
    0% { 
      opacity: 0; 
      transform: scale(1.1); 
      filter: saturate(1.4) brightness(1.05);
    }
    100% { 
      opacity: 1; 
      transform: scale(1); 
      filter: saturate(1) brightness(1);
    }
  }
  
  @keyframes gradientReveal {
    0% { 
      opacity: 0;
    }
    100% { 
      opacity: 1;
    }
  }
  
  @keyframes tagsSlideUp {
    0% { 
      opacity: 0;
      transform: translateY(8px);
      visibility: visible;
    }
    100% { 
      opacity: 1;
      transform: translateY(0);
      visibility: visible;
    }
  }
  
  .image-hidden {
    opacity: 0;
    transform: scale(1.1) translateZ(0);
    filter: saturate(1.4) brightness(1.05);
    will-change: transform, opacity, filter;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }
  
  .image-revealed {
    animation: imageReveal 0.7s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    transform: translateZ(0);
    will-change: transform, opacity, filter;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }
  
  .gradient-hidden {
    opacity: 0;
    visibility: hidden;
    will-change: opacity, visibility;
  }
  
  .gradient-revealed {
    opacity: 0;
    visibility: visible;
    animation: gradientReveal 0.7s cubic-bezier(0.4, 0, 0.2, 1) 0.3s forwards;
    will-change: opacity, visibility;
  }
  
  .tags-hidden {
    opacity: 0;
    transform: translateY(8px);
    visibility: hidden;
    will-change: transform, opacity, visibility;
  }
  
  .tags-revealed {
    opacity: 0;
    transform: translateY(8px);
    visibility: visible;
    animation: tagsSlideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.4s forwards;
    will-change: transform, opacity, visibility;
  }
`;

// Inject styles into document head if not already present
if (typeof document !== 'undefined' && !document.getElementById('recipe-card-animations')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'recipe-card-animations';
  styleElement.textContent = fadeInStyles;
  document.head.appendChild(styleElement);
}

/**
 * RecipeCard component - Displays a single recipe card
 * @param {Object} props
 * @param {Object} props.recipe - The recipe object
 * @param {number} props.index - Index of the recipe
 * @param {Function} props.onRecipeClick - Handler for clicking the recipe
 * @param {Object} props.imagesLoading - Loading state for images
 * @param {boolean} props.isRecipeReady - Whether the recipe and image are fully loaded
 * @param {boolean} props.isRecipeClickable - Whether the recipe can be opened (has instructions, even before image)
 * @param {boolean} props.isSavedRecipe - Whether this is a saved recipe with cooking actions
 * @param {Object} props.cookingState - Current cooking state { startedAt, isCompleted }
 * @param {Function} props.onStartCooking - Handler for starting cooking
 * @param {Function} props.onCompleteCooking - Handler for completing cooking
 * @param {Function} props.onDislikeRecipe - Handler for disliking/removing recipe
 * @param {boolean} props.groceryListLoaded - Whether the grocery list is loaded
 * @param {boolean} props.isGeneratingAlternatives - Whether alternatives are being generated
 * @param {number} props.servingsForDays - Number of days this meal is meant to last (1 or 2)
 * @param {boolean} props.fullWidth - Whether the card should take full width (for mobile vertical layout)
 * @param {boolean} props.isFavorite - Whether recipe is in favorites
 * @param {Function} props.onToggleFavorite - Handler for toggling recipe favorite state
 * @param {boolean} props.showFavoriteButton - Whether to show the favorite button
 * @param {boolean} props.showCookingActions - Whether to render saved-recipe cooking actions
 * @param {boolean} props.showPromoButton - Whether to show the admin promo button overlay
 * @param {Function} props.onAddToPromo - Handler to add this recipe to promo
 * @param {boolean} props.isAddingToPromo - Whether add-to-promo request is in progress
 */
const RecipeCard = ({
  recipe,
  index,
  onRecipeClick,
  imagesLoading,
  isRecipeReady = false,
  isRecipeClickable,
  isSavedRecipe = false,
  cookingState = null,
  onStartCooking = null,
  onCompleteCooking = null,
  onDislikeRecipe = null,
  groceryListLoaded = true,
  isGeneratingAlternatives = false,
  servingsForDays = 1,
  fullWidth = false,
  isFavorite = false,
  onToggleFavorite = null,
  showFavoriteButton = false,
  showCookingActions = true,
  showPromoButton = false,
  onAddToPromo = null,
  isAddingToPromo = false,
  mealCost = null,
  currencySymbol = '€',
}) => {
  const { t } = useTranslation('mealplan');
  const storageTime = recipe.maxStorageTime ? capDisplayFreshness(recipe.maxStorageTime) : '...';

  // Consumed-only estimated cost for this meal (excludes leftover waste).
  // Hidden when 0 / unavailable (e.g. custom meals with no grocery items).
  const mealCostLabel = mealCost > 0 ? `${currencySymbol}${mealCost.toFixed(2)}` : null;
  
  // Handle cookingTime - convert to string and remove "min" suffix if present
  const rawCookingTime = recipe.cookingTime ?? '';
  const cookingTime = String(rawCookingTime).replace(/\s*min\s*$/i, '').trim();
  
  const [imageLoaded, setImageLoaded] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const imgRef = useRef(null);

  // Reset imageLoaded when imageURL changes, then check if already cached
  useEffect(() => {
    setImageLoaded(false);
    
    // Use a small delay to ensure the img element has updated its src
    const timer = setTimeout(() => {
      if (imgRef.current && imgRef.current.complete && imgRef.current.naturalHeight !== 0) {
        setImageLoaded(true);
      }
    }, 50);
    
    return () => clearTimeout(timer);
  }, [recipe.imageURL]);

  // isRecipeClickable = can open drawer (has instructions), isRecipeReady = fully loaded with image
  // Fallback: if isRecipeClickable not provided, use isRecipeReady
  const canClick = isRecipeClickable !== undefined ? isRecipeClickable : isRecipeReady;

  const handleClick = (e) => {
    // Don't trigger onClick if clicking on action buttons or inside the cooking actions container
    if (e.target.closest('button') || e.target.closest('.cooking-actions')) {
      return;
    }
    if (canClick) {
      onRecipeClick(recipe);
    }
  };

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite(recipe);
    }
  };

  const handleStartCooking = (e) => {
    e.stopPropagation();
    const recipeId = recipe.id || recipe.title;
    console.log('🎯 RecipeCard: Start cooking clicked', {
      recipeId,
      recipe,
      onStartCooking: !!onStartCooking
    });
    if (onStartCooking) {
      onStartCooking(recipeId);
    }
  };

  const handleCompleteCooking = (e) => {
    e.stopPropagation();
    if (onCompleteCooking) {
      onCompleteCooking(recipe.id || recipe.title);
    }
  };

  const handleContinue = (e) => {
    e.stopPropagation();
    const recipeId = recipe.id || recipe.title;
    if (onStartCooking) {
      onStartCooking(recipeId);
    }
  };

  const handleRemoveRecipe = (e) => {
    e.stopPropagation();
    if (onDislikeRecipe) {
      onDislikeRecipe(recipe);
    }
  };

  const handleAddToPromo = (e) => {
    e.stopPropagation();
    if (onAddToPromo) {
      onAddToPromo(recipe);
    }
  };

  // Render cooking action buttons
  const renderCookingActions = () => {
    if (!isSavedRecipe || !showCookingActions) return null;
    
    if (cookingState?.isCompleted) {
      return (
        <div className="cooking-actions w-full flex flex-col items-stretch mt-3">
          <div className="flex gap-0">
            <div className="
                w-full h-[36px] px-4
                bg-[#EBEBEA] text-[#65635D]
                rounded-[6px]
                border-[0.5px] border-[#E2E1DF]
                text-center
                text-[12px] font-semibold leading-[26px]
                w-full
                flex items-center justify-center
            ">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className="mr-0.5">
                <path d="M16.6668 5L7.50016 14.1667L3.3335 10" stroke="#65635D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{t('actions.cooked', 'Cooked')}</span>
            </div>
          </div>
        </div>
      );
    }
    
    if (cookingState?.startedAt) {
      return (
        <div className="cooking-actions w-full flex flex-col items-stretch mt-3">
          <div className="flex gap-3">
            

            <button
              className="
                w-full h-[36px]
                bg-[#090] text-white
                rounded-[6px]
                border-[0.5px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                bg-[#090]
                text-[#FFF] text-center
                text-[12px] font-semibold leading-[20px]
                md:hover:border-[#060]
                md:hover:bg-[#060]
                w-full
                bg-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_1px_rgba(0,102,0,0.6)]
              "
              onClick={handleContinue}
            >
              
              <span
                className="inline-flex items-center pl-2 pr-3 py-1 text-[#FFF] text-center text-[12px] font-semibold leading-[20px]">
                <span className="mr-1 w-[24px]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M19.5254 6.66332C19.7113 6.21741 20.2239 6.00679 20.6699 6.19262C21.116 6.37851 21.3265 6.8911 21.1406 7.33715L19.5605 11.1272C19.7705 11.1417 19.9694 11.2313 20.1191 11.3811C20.2832 11.5452 20.375 11.7682 20.375 12.0002C20.375 14.2214 19.4925 16.3515 17.9219 17.9221C17.5679 18.2761 17.1838 18.5924 16.7783 18.8743C16.9409 19.0331 17.042 19.255 17.042 19.5002C17.042 19.9834 16.6501 20.3751 16.167 20.3752H7.83301C7.34991 20.3751 6.95801 19.9834 6.95801 19.5002C6.95801 19.2552 7.05843 19.0331 7.2207 18.8743C6.81551 18.5925 6.43189 18.2759 6.07812 17.9221C4.50751 16.3515 3.625 14.2214 3.625 12.0002C3.625 11.7682 3.71677 11.5452 3.88086 11.3811C4.04495 11.217 4.26794 11.1252 4.5 11.1252H17.667L19.5254 6.66332ZM5.43457 12.8752C5.62566 14.3092 6.28113 15.6505 7.31543 16.6848C8.55786 17.9272 10.2429 18.6252 12 18.6252C13.7571 18.6252 15.4421 17.9272 16.6846 16.6848C17.7189 15.6505 18.3743 14.3092 18.5654 12.8752H5.43457ZM10.5547 4.19653C10.7225 3.74339 11.2256 3.51215 11.6787 3.67993C12.2034 3.87426 12.941 4.54834 12.8643 5.68481L12.8652 5.68578C12.8258 6.34097 12.4451 6.79527 12.2734 7.01586C12.0545 7.29715 12.0507 7.33959 12.0488 7.37035L12.0479 7.38403C12.0361 7.53633 12.066 7.62948 12.1064 7.70532C12.1565 7.79903 12.2393 7.8976 12.3857 8.04028C12.7318 8.37753 12.7386 8.9315 12.4014 9.27758C12.0641 9.62351 11.5101 9.63039 11.1641 9.29321C10.7901 8.9288 10.2267 8.30536 10.3027 7.26098C10.3434 6.60988 10.7244 6.15675 10.8926 5.94067C11.1077 5.66427 11.1159 5.61772 11.1182 5.58129L11.1191 5.57055C11.1244 5.49732 11.1153 5.44796 11.1064 5.41723C11.0975 5.38607 11.0857 5.36258 11.0732 5.34496C11.0599 5.3262 11.0461 5.31292 11.0371 5.3059L10.9893 5.28539C10.5908 5.09025 10.3974 4.62147 10.5547 4.19653ZM14.7207 4.19653C14.878 3.77181 15.3306 3.5416 15.7598 3.65258L15.8457 3.67993L15.9463 3.72192C16.4607 3.96079 17.1041 4.62031 17.04 5.68578L17.0391 5.69653C16.9922 6.34545 16.6171 6.79458 16.4424 7.01782C16.2313 7.28761 16.2106 7.3445 16.2061 7.38598C16.1987 7.53294 16.2293 7.62594 16.2705 7.70239C16.3221 7.79816 16.4066 7.89784 16.5527 8.04028C16.8986 8.37754 16.9055 8.93155 16.5684 9.27758C16.2311 9.62367 15.6772 9.63044 15.3311 9.29321C14.9554 8.92714 14.393 8.30541 14.46 7.26098L14.4619 7.24243C14.5167 6.60142 14.8953 6.15489 15.0645 5.93871C15.2706 5.67526 15.2881 5.61743 15.292 5.57543C15.2957 5.50445 15.2886 5.45562 15.2793 5.42407C15.2695 5.39115 15.2556 5.36671 15.2422 5.34789C15.228 5.32801 15.2136 5.31427 15.2041 5.30688C15.1912 5.29689 15.1984 5.3061 15.2373 5.32055C14.7846 5.15257 14.5531 4.64945 14.7207 4.19653ZM6.3877 4.19653C6.54503 3.77171 6.99746 3.54141 7.42676 3.65258L7.5127 3.67993L7.61328 3.72192C8.12763 3.96089 8.77103 4.6205 8.70703 5.68578L8.70605 5.69653C8.65915 6.34539 8.28406 6.79456 8.10938 7.01782C7.89827 7.28762 7.87761 7.3445 7.87305 7.38598C7.86569 7.53288 7.89588 7.62465 7.93652 7.69946C7.98759 7.79338 8.07225 7.89236 8.21973 8.03246C8.56991 8.36532 8.58376 8.91947 8.25098 9.26977C7.91812 9.61999 7.36398 9.63381 7.01367 9.30102C6.62331 8.93013 6.05988 8.30754 6.12695 7.26098L6.12793 7.24243C6.18266 6.60139 6.56132 6.1549 6.73047 5.93871C6.93685 5.67493 6.95507 5.61745 6.95898 5.57543C6.96268 5.50446 6.95464 5.45562 6.94531 5.42407C6.93558 5.39132 6.92258 5.36666 6.90918 5.34789C6.89506 5.32815 6.88061 5.3143 6.87109 5.30688C6.85817 5.29686 6.86516 5.30603 6.9043 5.32055C6.45132 5.15266 6.22002 4.64958 6.3877 4.19653Z" fill="white"/>
                  </svg>
                </span>
                <span className="ml-0">
                  {t('actions.continueCooking')}
                </span>
              </span>
            </button>




            <button
              className="
                w-full h-[36px]
                bg-[#E2E1DF] text-[#0D0C07]
                rounded-[6px]
                border-[0.5px] border-[#E2E1DF]
                text-center
                text-[12px] font-semibold leading-[26px]
                md:hover:border-[#D7D7D5]
                md:hover:bg-[#D7D7D5]
                w-full
                shadow-[0_2px_16px_0_rgba(226,225,223,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_1px_rgba(215,215,213,0.6)]
              "
              onClick={handleCompleteCooking}
            >
              
              <span className="inline-flex items-center pl-2 pr-3 py-1 text-[#0D0C07] text-center text-[12px] font-semibold leading-[26px]">
                <span className="mr-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M18.0483 6.38104C18.39 6.03971 18.944 6.0396 19.2856 6.38104C19.6273 6.72275 19.6273 7.27761 19.2856 7.61932L10.1186 16.7853C9.95457 16.9494 9.73237 17.0421 9.50042 17.0422C9.26836 17.0422 9.04538 16.9494 8.88128 16.7853L4.71429 12.6193C4.37258 12.2776 4.37258 11.7227 4.71429 11.381C5.056 11.0393 5.61086 11.0393 5.95257 11.381L9.49945 14.9289L18.0483 6.38104Z" fill="#0D0C07"/>
                  </svg>
                </span>
                <span className="ml-0">
                  {t('actions.iAmDone')}
                </span>
              </span>
            </button>




          </div>
        </div>
      );
    }
    
    return (
      <div className="cooking-actions w-full flex flex-col items-stretch mt-3">
        <button
          className="
            w-full h-[36px]
            bg-[#090]
            rounded-[6px]
            border-[0.5px] border-[#006600]
            text-[#006600] text-center
            text-[12.8px] font-semibold leading-[26px]
            md:hover:border-[#060]
            md:hover:bg-[#060]
            md:hover:text-[#fff]
            md:hover:fill-[#fff]
            w-full
            bg-[#fff]
            shadow-[0_0px_0px_1px_rgba(0,102,0,0.4)]
            fill-[#006600]
          "
          onClick={handleStartCooking}
        >
          
          <span
            className={`inline-flex items-center
                pl-2 pr-3 py-1`}
          >
            <span className="mr-1 w-[24px]">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <path d="M19.5254 6.66332C19.7113 6.21741 20.2239 6.00679 20.6699 6.19262C21.116 6.37851 21.3265 6.8911 21.1406 7.33715L19.5605 11.1272C19.7705 11.1417 19.9694 11.2313 20.1191 11.3811C20.2832 11.5452 20.375 11.7682 20.375 12.0002C20.375 14.2214 19.4925 16.3515 17.9219 17.9221C17.5679 18.2761 17.1838 18.5924 16.7783 18.8743C16.9409 19.0331 17.042 19.255 17.042 19.5002C17.042 19.9834 16.6501 20.3751 16.167 20.3752H7.83301C7.34991 20.3751 6.95801 19.9834 6.95801 19.5002C6.95801 19.2552 7.05843 19.0331 7.2207 18.8743C6.81551 18.5925 6.43189 18.2759 6.07812 17.9221C4.50751 16.3515 3.625 14.2214 3.625 12.0002C3.625 11.7682 3.71677 11.5452 3.88086 11.3811C4.04495 11.217 4.26794 11.1252 4.5 11.1252H17.667L19.5254 6.66332ZM5.43457 12.8752C5.62566 14.3092 6.28113 15.6505 7.31543 16.6848C8.55786 17.9272 10.2429 18.6252 12 18.6252C13.7571 18.6252 15.4421 17.9272 16.6846 16.6848C17.7189 15.6505 18.3743 14.3092 18.5654 12.8752H5.43457ZM10.5547 4.19653C10.7225 3.74339 11.2256 3.51215 11.6787 3.67993C12.2034 3.87426 12.941 4.54834 12.8643 5.68481L12.8652 5.68578C12.8258 6.34097 12.4451 6.79527 12.2734 7.01586C12.0545 7.29715 12.0507 7.33959 12.0488 7.37035L12.0479 7.38403C12.0361 7.53633 12.066 7.62948 12.1064 7.70532C12.1565 7.79903 12.2393 7.8976 12.3857 8.04028C12.7318 8.37753 12.7386 8.9315 12.4014 9.27758C12.0641 9.62351 11.5101 9.63039 11.1641 9.29321C10.7901 8.9288 10.2267 8.30536 10.3027 7.26098C10.3434 6.60988 10.7244 6.15675 10.8926 5.94067C11.1077 5.66427 11.1159 5.61772 11.1182 5.58129L11.1191 5.57055C11.1244 5.49732 11.1153 5.44796 11.1064 5.41723C11.0975 5.38607 11.0857 5.36258 11.0732 5.34496C11.0599 5.3262 11.0461 5.31292 11.0371 5.3059L10.9893 5.28539C10.5908 5.09025 10.3974 4.62147 10.5547 4.19653ZM14.7207 4.19653C14.878 3.77181 15.3306 3.5416 15.7598 3.65258L15.8457 3.67993L15.9463 3.72192C16.4607 3.96079 17.1041 4.62031 17.04 5.68578L17.0391 5.69653C16.9922 6.34545 16.6171 6.79458 16.4424 7.01782C16.2313 7.28761 16.2106 7.3445 16.2061 7.38598C16.1987 7.53294 16.2293 7.62594 16.2705 7.70239C16.3221 7.79816 16.4066 7.89784 16.5527 8.04028C16.8986 8.37754 16.9055 8.93155 16.5684 9.27758C16.2311 9.62367 15.6772 9.63044 15.3311 9.29321C14.9554 8.92714 14.393 8.30541 14.46 7.26098L14.4619 7.24243C14.5167 6.60142 14.8953 6.15489 15.0645 5.93871C15.2706 5.67526 15.2881 5.61743 15.292 5.57543C15.2957 5.50445 15.2886 5.45562 15.2793 5.42407C15.2695 5.39115 15.2556 5.36671 15.2422 5.34789C15.228 5.32801 15.2136 5.31427 15.2041 5.30688C15.1912 5.29689 15.1984 5.3061 15.2373 5.32055C14.7846 5.15257 14.5531 4.64945 14.7207 4.19653ZM6.3877 4.19653C6.54503 3.77171 6.99746 3.54141 7.42676 3.65258L7.5127 3.67993L7.61328 3.72192C8.12763 3.96089 8.77103 4.6205 8.70703 5.68578L8.70605 5.69653C8.65915 6.34539 8.28406 6.79456 8.10938 7.01782C7.89827 7.28762 7.87761 7.3445 7.87305 7.38598C7.86569 7.53288 7.89588 7.62465 7.93652 7.69946C7.98759 7.79338 8.07225 7.89236 8.21973 8.03246C8.56991 8.36532 8.58376 8.91947 8.25098 9.26977C7.91812 9.61999 7.36398 9.63381 7.01367 9.30102C6.62331 8.93013 6.05988 8.30754 6.12695 7.26098L6.12793 7.24243C6.18266 6.60139 6.56132 6.1549 6.73047 5.93871C6.93685 5.67493 6.95507 5.61745 6.95898 5.57543C6.96268 5.50446 6.95464 5.45562 6.94531 5.42407C6.93558 5.39132 6.92258 5.36666 6.90918 5.34789C6.89506 5.32815 6.88061 5.3143 6.87109 5.30688C6.85817 5.29686 6.86516 5.30603 6.9043 5.32055C6.45132 5.15266 6.22002 4.64958 6.3877 4.19653Z"/>
                  </svg>
            </span>
            <span className="ml-0">
              {t('recipeCard.startCooking')}
            </span>
          </span>
        </button>
      </div>
    );
  };

  // Recipe has full details from the progressive processor (instructions, description, etc.)
  // Saved recipes always have details (loaded from storage), so skip the skeleton for them.
  const hasDetails = isSavedRecipe || (!recipe.isPlaceholder && recipe.instructions && recipe.instructionsFormatted);

  // Trigger fade-in transition — use double-rAF to guarantee the browser
  // has painted the element at opacity-0 before we flip to opacity-100.
  useEffect(() => {
    if (hasDetails) {
      let cancelled = false;
      // First rAF: element is in the DOM at opacity-0 after this paint
      requestAnimationFrame(() => {
        if (cancelled) return;
        // Second rAF: now flip to opacity-100 — transition will fire
        requestAnimationFrame(() => {
          if (!cancelled) setContentVisible(true);
        });
      });
      return () => { cancelled = true; };
    } else {
      setContentVisible(false);
    }
  }, [hasDetails]);

  return (
    <div onClick={handleClick}
      className={`${!hasDetails ? 'cursor-default' : canClick ? 'cursor-pointer' : 'cursor-not-allowed'} transition-all duration-500 ease-in-out flex-1 ${fullWidth ? 'w-full' : 'w-[315px]'} md:p-5 p-4 pb-5 overflow-hidden rounded-[12px] border-[0.5px] border-[var(--Color-Grays-Gray-0,#ffffff)] ${canClick ? 'md:hover:border-[var(--Color-Grays-Gray-10,#0D0C07)]' : ''} bg-[var(--Color-Grays-Gray-0,#FFF)] shadow-[0px_1px_2px_0px_rgba(35,33,22,0.04),_0px_6px_12px_0px_rgba(83,81,69,0.04)] flex flex-col ${
        recipe.isPlaceholder ? 'animate-pulse' : ''
      }`}
    >
      {/* Image area — skeleton / spinner / actual image */}
      <div className="relative w-full overflow-hidden aspect-[16/10]">
        {/* Grey placeholder background — always present until image loads */}
        <div className="absolute inset-0 bg-[#F3F3F2] rounded-[6px] flex items-center justify-center">
          {/* Show lottie preloader when waiting for details or image */}
          {!recipe.isPlaceholder && !recipe.imageFailed && (
            <div className={`flex flex-col items-center gap-1 transition-opacity duration-300 ${recipe.imageURL && imageLoaded ? 'opacity-0' : 'opacity-100'}`}>
              <DotLottieReact
                src="/assets/recipe-preloader.lottie"
                loop
                autoplay
                className="w-20 h-20"
              />
              <span className="text-[#A09F9C] text-sm text-center">
                {!hasDetails ? t('recipeCard.craftingRecipe') : t('recipeCard.creatingPhoto')}
              </span>
            </div>
          )}
        </div>

        {/* Actual image — renders on top when URL is available */}
        {recipe.imageURL && (
          <div className="absolute inset-0 rounded-[6px] overflow-hidden cursor-pointer">
            {showFavoriteButton && !recipe.isPlaceholder && (
              <button
                type="button"
                onClick={handleToggleFavorite}
                className={`absolute top-1 left-1 z-10 w-7 h-7 rounded-[4px] ${isFavorite ? 'bg-[rgba(255,255,255,0.8)]' : 'bg-[rgba(255,255,255,0.3)]'}   backdrop-blur-[8px] flex items-center justify-center transition-colors`}
                aria-label={isFavorite ? t('recipeCard.removeFromFavorites') : t('recipeCard.addToFavorites')}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ filter: 'drop-shadow(0 0 4px rgba(0, 0, 0, 0.4))' }}
                >
                  <path
                    d="M20.8402 5.60996C20.3292 5.09891 19.7225 4.6935 19.0548 4.41689C18.3872 4.14028 17.6716 3.99792 16.9489 3.99792C16.2262 3.99792 15.5106 4.14028 14.843 4.41689C14.1754 4.6935 13.5686 5.09891 13.0577 5.60996L11.9977 6.66996L10.9377 5.60996C9.90598 4.57827 8.50668 3.99867 7.04767 3.99867C5.58866 3.99867 4.18935 4.57827 3.15767 5.60996C2.12598 6.64164 1.54639 8.04095 1.54639 9.49996C1.54639 10.959 2.12598 12.3583 3.15767 13.39L4.21767 14.45L11.9977 22.23L19.7777 14.45L20.8377 13.39C21.3487 12.879 21.7541 12.2722 22.0307 11.6046C22.3074 10.937 22.4497 10.2214 22.4497 9.49871C22.4497 8.77601 22.3074 8.06045 22.0307 7.39279C21.7541 6.72513 21.3487 6.11842 20.8377 5.60746L20.8402 5.60996Z"
                    stroke={isFavorite ? 'rgba(255, 187, 187, 0.5)' : 'rgba(0, 0, 0, 0.1)'}
                    strokeWidth="1"
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    fill={isFavorite ? '#ff1010' : 'rgba(255, 255, 255, 1)'}
                  />
                </svg>
              </button>
            )}
            {showPromoButton && !recipe.isPlaceholder && (
              <button
                type="button"
                onClick={handleAddToPromo}
                disabled={isAddingToPromo}
                className="absolute top-1 right-1 z-10 h-7 px-2 rounded-[6px] bg-[rgba(13,12,7,0.6)] text-white text-[11px] font-semibold backdrop-blur-[8px] disabled:opacity-60"
                aria-label="Add recipe to promo"
                title="Add to promo"
              >
                {isAddingToPromo ? 'Saving...' : 'Add to Promo'}
              </button>
            )}
            <img 
              ref={imgRef}
              src={recipe.imageURL}
              alt={recipe.title}
              className={`absolute inset-0 w-full h-full object-cover ${imageLoaded ? 'image-revealed' : 'image-hidden'}`}
              loading={isSavedRecipe ? "eager" : "lazy"}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div 
              className={`absolute inset-0 pointer-events-none ${imageLoaded && hasDetails ? 'gradient-revealed' : 'gradient-hidden'}`}
              style={{ background: 'linear-gradient(180deg, rgba(243, 243, 242, 0) 72%, rgba(13, 12, 7, 0.28) 100%)' }}
            />
            {/* "For 2 days" tag - top right */}
            {servingsForDays === 2 && (
              <div className={`absolute top-1 right-1 ${imageLoaded && hasDetails ? 'tags-revealed' : 'tags-hidden'}`}>
                <span className="px-2 py-1 text-white rounded-[6px] border-[0px] bg-[rgba(0,102,0,0.9)] backdrop-blur-lg text-xs font-medium">
                  {t('forTwoDays')}
                </span>
              </div>
            )}
            {/* Tags - inside image container, bottom left */}
            <div className={`absolute bottom-1 left-1 flex gap-0.5 ${imageLoaded && hasDetails ? 'tags-revealed' : 'tags-hidden'}`}>
              <span className="px-2 py-1 text-white rounded-[6px] border-[0px] border-[#0D0C07] bg-[rgba(37,34,24,0.64)] backdrop-blur-lg text-xs font-medium">
                {t('recipeCard.freshFor', { days: storageTime })}
              </span>
              {cookingTime && (
                <span className="px-2 py-1 text-white rounded-[6px] border-[0px] border-[#0D0C07] bg-[rgba(37,34,24,0.64)] backdrop-blur-lg text-xs font-medium">
                  {t('recipeCard.min', { time: cookingTime })}
                </span>
              )}
              {mealCostLabel && (
                <span className="px-2 py-1 text-white rounded-[6px] border-[0px] border-[#0D0C07] bg-[rgba(37,34,24,0.64)] backdrop-blur-lg text-xs font-medium">
                  {mealCostLabel}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Content Section — real content drives height, skeleton overlays */}
      <div className="flex flex-col flex-1 relative">
        {/* Real content — always in flow (drives container height), fades in */}
        <h3 className={`text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[13.6px] font-semibold tracking-[-0.01em] leading-[22px] mb-1 pt-4 line-clamp-1 transition-opacity duration-500 ease-in-out ${contentVisible ? 'opacity-100' : 'opacity-0'}`}>{recipe.title}</h3>
        <p className={`text-[var(--Color-Grays-Gray-8,#56554D)] text-[13px] font-normal leading-[22px] mb-0 line-clamp-2 transition-opacity duration-500 ease-in-out ${contentVisible ? 'opacity-100' : 'opacity-0'}`}>{recipe.description}</p>
        
        {/* Remove Recipe button — fades in when grocery list ready */}
        {!isSavedRecipe && onDislikeRecipe && (
          <div className={`flex justify-start mt-4 relative transition-opacity duration-500 ease-in-out ${contentVisible && groceryListLoaded ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <button
              onClick={handleRemoveRecipe}
              className={`
                font-semibold w-full bg-[#F3F3F2] text-[12px] leading-[20px] border-[2px] border-[rgba(255,255,2555,1)] rounded-[6px] px-3 h-[32px] shadow-[0_0px_0px_1px_rgba(212,11,11,0.32)]
                transition-colors
                ${isGeneratingAlternatives 
                  ? 'text-[#A09F9C] border-[#E2E1DF] cursor-not-allowed' 
                  : 'text-[#4D4B42] md:hover:text-[#D40B0B] border-[#D40B0B]'
                }
              `}
              disabled={isGeneratingAlternatives}
            >
              {isGeneratingAlternatives ? t('loading.updating') : t('recipeCard.iDontLikeIt')}
            </button>
          </div>
        )}
        
        {/* Cooking Actions for Saved Recipes */}
        {renderCookingActions()}

        {/* Skeleton overlay — mirrors real content layout exactly, fades out */}
        <div className={`absolute inset-0 pt-4 flex flex-col pointer-events-none transition-opacity duration-300 ease-in-out ${contentVisible ? 'opacity-0' : 'opacity-100'}`}>
          {/* Mirrors h3: leading-[22px] mb-1 */}
          <div className="h-[22px] flex items-center mb-1"><div className="h-3.5 bg-[#E8E8E7] rounded w-3/4"></div></div>
          {/* Mirrors p line 1: leading-[22px] */}
          <div className="h-[22px] flex items-center"><div className="h-3 bg-[#E8E8E7] rounded w-full"></div></div>
          {/* Mirrors p line 2: leading-[22px] */}
          <div className="h-[22px] flex items-center"><div className="h-3 bg-[#E8E8E7] rounded w-2/3"></div></div>
          {/* Mirrors Replace Meal button: mt-4 h-[32px] */}
          {!isSavedRecipe && onDislikeRecipe && (
            <div className="mt-4 h-[32px] bg-[#E8E8E7] rounded-[6px] w-full"></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipeCard;
